import { useMemo } from 'react';
import { Text } from '@chakra-ui/react';
import { ShiftPickerModal } from './ShiftPickerModal';
import { formatShiftLabel, shiftChangeOptions, type EventShift } from '@/utils/eventShifts';

interface ChangeShiftModalProps {
  isOpen: boolean;
  /** The shift being moved away from. */
  fromShift: EventShift | null;
  /** Every shift on the event. */
  shifts: EventShift[];
  submitting?: boolean;
  onClose: () => void;
  onConfirm: (toShiftId: string) => void;
}

/**
 * Pick the one shift to move to. The move itself is a single `change_shift` call, so a shift
 * that fills up while this is open is refused without costing the volunteer the one they have.
 */
export function ChangeShiftModal({ isOpen, fromShift, shifts, submitting, onClose, onConfirm }: ChangeShiftModalProps) {
  const { options, notes } = useMemo(
    () => (fromShift ? shiftChangeOptions(shifts, fromShift.id) : { options: [], notes: {} }),
    [shifts, fromShift],
  );

  if (!fromShift) return null;

  return (
    <ShiftPickerModal
      isOpen={isOpen}
      title="Change shift"
      intro={
        <>
          Moving from{' '}
          <Text as="span" fontWeight="semibold" color="volyx.text">
            {formatShiftLabel(fromShift)}
          </Text>
          . If the shift you pick fills up first, you keep this one.
        </>
      }
      options={options}
      notes={notes}
      emptyText="There is no other upcoming shift on this event to move to."
      confirmLabel={() => 'Move here'}
      submitting={submitting}
      onClose={onClose}
      onConfirm={(ids) => ids[0] && onConfirm(ids[0])}
    />
  );
}
